import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Set per-route <title>, <meta name="description">, <link rel="canonical">,
 * and matching OpenGraph / Twitter tags. React SPA on a static host:
 * every route serves the same index.html, so we rewrite these on mount.
 *
 * The hook removes any existing canonical/description/title tags before
 * inserting the fresh one so crawlers never see two conflicting canonicals.
 */
const PROD_ORIGIN = "https://websitetech.pro";

function upsertMeta(selector, attr, value, content) {
  // Remove duplicates to guarantee a single tag per key.
  const all = document.head.querySelectorAll(selector);
  all.forEach((n, i) => { if (i > 0) n.remove(); });
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}

function upsertLink(rel, href) {
  const all = document.head.querySelectorAll(`link[rel="${rel}"]`);
  all.forEach((n, i) => { if (i > 0) n.remove(); });
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  return el;
}

export function usePageSeo({ title, description, canonical, image, noindex } = {}) {
  const location = useLocation();
  useEffect(() => {
    if (!title && !description && !canonical) return;
    const resolvedCanonical = canonical || `${PROD_ORIGIN}${location.pathname}`;
    if (title) document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', "name", "description", description);
      upsertMeta('meta[property="og:description"]', "property", "og:description", description);
      upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', "property", "og:title", title);
      upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    }
    upsertLink("canonical", resolvedCanonical);
    upsertMeta('meta[property="og:url"]', "property", "og:url", resolvedCanonical);
    if (image) {
      upsertMeta('meta[property="og:image"]', "property", "og:image", image);
      upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image);
    }
    upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"
    );
  }, [title, description, canonical, image, noindex, location.pathname]);
}

/** Inject a JSON-LD BreadcrumbList into <head>. */
export function useBreadcrumbSchema(trail) {
  useEffect(() => {
    if (!trail || !trail.length) return;
    const data = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: trail.map((t, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: t.name,
        item: t.url ? (t.url.startsWith("http") ? t.url : `${PROD_ORIGIN}${t.url}`) : undefined,
      })),
    };
    let el = document.head.querySelector('script[data-breadcrumb-schema="1"]');
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-breadcrumb-schema", "1");
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { el?.remove?.(); };
  }, [JSON.stringify(trail)]);
}

/**
 * Inject a JSON-LD FAQPage into <head>.
 * `questions`: array of { question, answer }
 */
export function useFaqSchema(questions) {
  useEffect(() => {
    if (!questions || !questions.length) return;
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: questions.map((q) => ({
        "@type": "Question",
        name: q.question,
        acceptedAnswer: { "@type": "Answer", text: q.answer },
      })),
    };
    let el = document.head.querySelector('script[data-faq-schema="1"]');
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-faq-schema", "1");
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => { el?.remove?.(); };
  }, [JSON.stringify(questions)]);
}

/**
 * Inject a JSON-LD Service into <head>.
 * `data`: { name, description, serviceType, areaServed?, provider?, url? }
 */
export function useServiceSchema(data) {
  const location = useLocation();
  useEffect(() => {
    if (!data || !data.name) return;
    const url = data.url || `${PROD_ORIGIN}${location.pathname}`;
    const payload = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${url}#service`,
      name: data.name,
      description: data.description,
      serviceType: data.serviceType || data.name,
      areaServed: data.areaServed || "Worldwide",
      provider: data.provider || {
        "@type": "Organization",
        name: "WebsiteTech.pro",
        url: PROD_ORIGIN,
      },
      url,
    };
    let el = document.head.querySelector('script[data-service-schema="1"]');
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-service-schema", "1");
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
    return () => { el?.remove?.(); };
  }, [JSON.stringify(data), location.pathname]);
}

/**
 * Inject a JSON-LD Article into <head>.
 * `data`: { headline, description, datePublished?, dateModified?, author?, image? }
 */
export function useArticleSchema(data) {
  const location = useLocation();
  useEffect(() => {
    if (!data || !data.headline) return;
    const url = `${PROD_ORIGIN}${location.pathname}`;
    const payload = {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": `${url}#article`,
      headline: data.headline,
      description: data.description,
      datePublished: data.datePublished,
      dateModified: data.dateModified || data.datePublished,
      author: data.author || { "@type": "Organization", name: "WebsiteTech.pro" },
      publisher: { "@type": "Organization", name: "WebsiteTech.pro", url: PROD_ORIGIN },
      image: data.image,
      mainEntityOfPage: url,
    };
    let el = document.head.querySelector('script[data-article-schema="1"]');
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute("data-article-schema", "1");
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
    return () => { el?.remove?.(); };
  }, [JSON.stringify(data), location.pathname]);
}

/**
 * Inject hreflang <link> alternates into <head>.
 * `languages`: array of { lang: "en", url: "/path" | "https://..." }
 * Automatically also writes an x-default alternate pointing to the first entry.
 */
export function useHreflangAlternates(languages) {
  const location = useLocation();
  useEffect(() => {
    if (!languages || !languages.length) return;
    // Remove previous hreflang tags we own.
    document.head.querySelectorAll('link[data-hreflang-managed="1"]').forEach((n) => n.remove());
    const absolute = (u) => u.startsWith("http") ? u : `${PROD_ORIGIN}${u}`;
    languages.forEach((l) => {
      const el = document.createElement("link");
      el.setAttribute("rel", "alternate");
      el.setAttribute("hreflang", l.lang);
      el.setAttribute("href", absolute(l.url));
      el.setAttribute("data-hreflang-managed", "1");
      document.head.appendChild(el);
    });
    // x-default: the first entry wins.
    const defEl = document.createElement("link");
    defEl.setAttribute("rel", "alternate");
    defEl.setAttribute("hreflang", "x-default");
    defEl.setAttribute("href", absolute(languages[0].url));
    defEl.setAttribute("data-hreflang-managed", "1");
    document.head.appendChild(defEl);
  }, [JSON.stringify(languages), location.pathname]);
}

export default usePageSeo;
